import type {
  AdminAuthResult,
  AdminAuthStatus,
  AdminConnection,
  OverviewData,
  UserDetailData,
  UserListData,
  UserStatus,
} from "./types";

export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "AdminApiError";
  }
}

function endpoint(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, "")}${path}`;
}

async function request<T>(
  baseUrl: string,
  path: string,
  sessionToken?: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(endpoint(baseUrl, path), {
    ...init,
    headers: {
      Accept: "application/json",
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      ...init?.headers,
    },
  });
  const body = await response.json().catch(() => null) as
    | { message?: string; error?: string }
    | T
    | null;
  if (!response.ok) {
    const error = body as { message?: string; error?: string } | null;
    throw new AdminApiError(
      error?.message ?? `请求失败（${response.status}）`,
      response.status,
      error?.error,
    );
  }
  return body as T;
}

export const adminApi = {
  authStatus: (baseUrl: string) =>
    request<AdminAuthStatus>(baseUrl, "/api/admin/auth/status"),

  setup: (
    baseUrl: string,
    bootstrapKey: string,
    input: { displayName: string; email: string; password: string },
  ) => request<AdminAuthResult>(baseUrl, "/api/admin/auth/setup", undefined, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Key": bootstrapKey,
    },
    body: JSON.stringify(input),
  }),

  login: (baseUrl: string, input: { email: string; password: string }) =>
    request<AdminAuthResult>(baseUrl, "/api/admin/auth/login", undefined, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }),

  me: (connection: AdminConnection) =>
    request<{ admin: AdminConnection["admin"]; expiresAt: string }>(
      connection.baseUrl,
      "/api/admin/auth/me",
      connection.sessionToken,
    ),

  logout: (connection: AdminConnection) =>
    request<{ ok: true }>(
      connection.baseUrl,
      "/api/admin/auth/logout",
      connection.sessionToken,
      { method: "POST" },
    ),

  overview: (connection: AdminConnection) =>
    request<OverviewData>(connection.baseUrl, "/api/admin/overview", connection.sessionToken),

  users: (
    connection: AdminConnection,
    query: { page: number; pageSize: number; search: string; status: string },
  ) => {
    const params = new URLSearchParams({
      page: String(query.page),
      pageSize: String(query.pageSize),
      search: query.search,
      status: query.status,
    });
    return request<UserListData>(connection.baseUrl, `/api/admin/users?${params}`, connection.sessionToken);
  },

  user: (connection: AdminConnection, userId: string) =>
    request<UserDetailData>(connection.baseUrl, `/api/admin/users/${encodeURIComponent(userId)}`, connection.sessionToken),

  setUserStatus: (
    connection: AdminConnection,
    userId: string,
    status: Extract<UserStatus, "active" | "disabled">,
  ) => request<{ ok: true; status: UserStatus }>(
    connection.baseUrl,
    `/api/admin/users/${encodeURIComponent(userId)}/status`,
    connection.sessionToken,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    },
  ),
};
